// Ported from eb-ui `src/legacy/CardsCarousel/CardsCarousel.tsx` — the
// "More collections from this organizer" + "Other events you may like"
// carousels.
//
// REWRITTEN on the vendored `@eventbrite/collection-carousel` `SimpleCarousel`
// (the missing `@eventbrite/eds-carousel` `Carousel` + the bespoke 2-up/1-up
// desktop/mobile wrapper are replaced by one scroll-snap carousel). Same
// public API so `Collection.tsx` / `CollectionError.tsx` call-sites are
// unchanged: `CardsCarousel` + `CardsType` (COLLECTION/EVENT) + the
// `formatCards`/`formatEvents`/`formatCollections` helpers.
//
// Mapping:
//   - `@eventbrite/eds-carousel` `Carousel` → `SimpleCarousel` (slidesVisible
//     tablet 2 / desktop 3 (events) or 2 (collections); useSideNavigation;
//     headerTitle; fullyEnableNavigation — legacy desktop counts: EVENTS=3,
//     COLLECTIONS=2; mobile=1 is SimpleCarousel's default single-slide).
//   - `@eventbrite/event-renderer` `EventCard` (MISSING) → the local
//     `../lib/eds/EventCard` (sanctioned-primitive card, the legacy recipe
//     look). `CONSUMER_CARD_DEFAULTS` (missing) → inlined at the call-site
//     (isTargetBlank + isLazyImage + shouldConvertToUserTimezone).
//   - `@eventbrite/event-renderer` `EventClient`/`EventClientProvider`
//     (ABSENT from the sanctioned package) → DROPPED (a module-global
//     fetcher; nothing in this tree reads its context).
//   - `@eventbrite/creator-collection-card` → the vendored shared module
//     (same `CreatorCollectionCard` contract).
//   - `@eventbrite/social-share` → the vendored shared module.
//   - `@eventbrite/legacy-infra.i18n` `GenericLazyString` → plain `string`
//     (`@/lib/gettext` returns a string).
//   - the `updateEventData` origin→destination upgrade is kept (same
//     `../lib/data` fn `CollectionEvents` uses).
import { SimpleCarousel } from '@eventbrite/collection-carousel';
import { CreatorCollectionCard } from '@eventbrite/creator-collection-card';
import {
    transformDestinationEvent,
    type DestinationEvent,
    type FormattedEvent,
} from '../lib/eventRenderer';
import {
    getLabelFromShareType,
    trackShareAction,
    type ShareType,
} from '@eventbrite/social-share';
import React, { useContext, useEffect, useState } from 'react';
import { formatUrl } from 'url-lib';

import { EventCard, type EventCardProps } from '../lib/eds/EventCard';
import { updateEventData } from '../lib/data';
import { AnalyticsContext, ApplicationContext } from '../context';
import {
    AffiliateCodes,
    CollectionCardOriginType,
    CollectionServerData,
    EventOriginType,
} from '@/types';
import {
    AnalyticsAction,
    AnalyticsCategory,
    initSignalProp,
    trackEvent,
} from '../_shared';
import {
    transformCollectionCard,
    transformCollectionEvent,
} from '../_shared/transform';
import { gettext } from '@/lib/gettext';
import type { SlideConfig } from '@eventbrite/collection-carousel';
// (The `.carousel-cards` global rules are loaded from `_app` via
// `src/styles/cc.scss` — Turbopack forbids global (non-module) first-party CSS
// imports from a component file.)

export type OriginCards =
    | EventOriginType[]
    | CollectionCardOriginType[]
    | DestinationEvent[];

export interface RelatedCardsProps {
    affCode?: AffiliateCodes;
    cards: OriginCards;
    cardsType: CardsType;
    newTitleStyle?: boolean;
    organizerId: string;
    title?: string;
    isFromDestination?: boolean;
    dataTestId?: string;
}

export type RelatedCardsChildrenProps = Omit<RelatedCardsProps, 'organizerId'>;

export enum CardsType {
    COLLECTION = 'collection',
    EVENT = 'event',
}

export interface CreatorCollectionCardCarouselProps {
    id: string;
    organizerId?: string;
    slug: string;
    name: string;
    summary?: string;
    imageUrl?: string;
    numUpcomingEvents?: number;
}

function useExtendedEventData(
    initialEvents: FormattedEvent[],
): FormattedEvent[] {
    const [events, setEvents] = useState(initialEvents);
    useEffect(() => {
        const eventIdList = events.map((event) => event.id);
        updateEventData(eventIdList)
            .then((newUpcomingEventsData) => {
                if (newUpcomingEventsData?.events.length > 0) {
                    setEvents(newUpcomingEventsData.events);
                }
            })
            .catch((error: unknown) => {
                // display-only enrichment — keep the seeded events on failure.
                console.error(
                    'cards-carousel destination upgrade failed',
                    error,
                );
            }); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setEvents]);
    return events;
}

const _getEventCards = ({
    updatedEvents,
    affCode,
    context,
    pageOwnerOrganizerId,
}: {
    updatedEvents: FormattedEvent[];
    affCode?: AffiliateCodes;
    context?: CollectionServerData;
    pageOwnerOrganizerId: string;
}) => {
    return updatedEvents.map((event: FormattedEvent) => {
        const shareEventProps = {
            eventId: event.id,
            eventName: event.name,
            eventUrl: event.url,
            ...context?.share_props,
        };

        // Prop to set or not the signal data on the EventCard component
        const signalProp = initSignalProp(
            pageOwnerOrganizerId as string,
            event.organizerId,
        );

        // The app's `share_props` (local loose `ShareProps`) → the local
        // EventCard's `ShareOptionsLike` (strict `facebookOptions`). The
        // `facebookOptions` may be absent in the local type; the EventCard
        // tolerates a missing `facebookOptions` (it defaults).
        const shareOptionsForCard: EventCardProps['shareOptions'] =
            context?.share_props ? (shareEventProps as EventCardProps['shareOptions']) : undefined;

        return (
            <EventCard
                // CONSUMER_CARD_DEFAULTS (missing from the sanctioned
                // package) — the legacy recipe's defaults for a consumer
                // event card.
                isTargetBlank
                shouldConvertToUserTimezone
                isLazyImage
                key={event.id}
                affCode={affCode}
                event={event}
                gaCategory={AnalyticsCategory.RelatedEvents}
                isAuthenticated={context?.user.isAuthenticated || false}
                shareOptions={shareOptionsForCard}
                description={
                    event.isRepeatingEvent ? (
                        <div className="eds-text-color--ui-orange">
                            {gettext('More dates available')}
                        </div>
                    ) : undefined
                }
                onDidShareClick={(shareType: string) =>
                    trackShareAction({
                        label: getLabelFromShareType(shareType as ShareType),
                        category: AnalyticsCategory.EventCard,
                    })
                }
                onWillShareClick={() =>
                    trackShareAction({
                        label: 'open-modal',
                        category: AnalyticsCategory.EventCard,
                    })
                }
                {...signalProp}
            />
        );
    });
};

const _getCollectionCards = ({
    collections,
    affCode,
}: {
    collections: CreatorCollectionCardCarouselProps[];
    affCode?: AffiliateCodes;
}) => {
    return collections.map((collection) => {
        const { id, name, slug, summary, imageUrl, numUpcomingEvents } =
            collection;

        const handleClick = () => {
            trackEvent({
                action: AnalyticsAction.CollectionView,
                category: AnalyticsCategory.RelatedEvents,
                label: 'more-collections-from-this-organizer',
            });
        };

        return (
            <CreatorCollectionCard
                key={id}
                id={id}
                name={name}
                slug={
                    affCode
                        ? formatUrl(slug, {
                              aff: affCode,
                          })
                        : slug
                }
                summary={summary}
                imageUrl={imageUrl}
                onClick={handleClick}
                numUpcomingEvents={numUpcomingEvents}
            />
        );
    });
};

const getCardMap = ({
    cardsType,
    formattedCards,
    affCode,
    updatedEvents,
    context,
    pageOwnerOrganizerId,
}: {
    cardsType: CardsType;
    formattedCards: FormattedEvent[] | CreatorCollectionCardCarouselProps[];
    affCode?: AffiliateCodes;
    updatedEvents?: FormattedEvent[];
    context?: CollectionServerData;
    pageOwnerOrganizerId?: string;
}): React.ReactElement[] => {
    if (formattedCards === undefined) {
        return [];
    }

    return cardsType === CardsType.EVENT
        ? _getEventCards({
              updatedEvents: updatedEvents as FormattedEvent[],
              affCode,
              context,
              pageOwnerOrganizerId: pageOwnerOrganizerId || '',
          })
        : _getCollectionCards({
              collections:
                  formattedCards as CreatorCollectionCardCarouselProps[],
              affCode,
          });
};

const formatEventsFromDestination = (cards: DestinationEvent[]) => {
    return cards.map((event: DestinationEvent) =>
        transformDestinationEvent(event),
    );
};

const formatEventsFromOrigin = (cards: EventOriginType[]) => {
    return cards.map((event: EventOriginType) =>
        transformCollectionEvent(event),
    );
};

export const formatEvents = (
    cards: EventOriginType[] | DestinationEvent[],
    isFromDestination?: boolean,
): FormattedEvent[] =>
    isFromDestination
        ? formatEventsFromDestination(cards as DestinationEvent[])
        : formatEventsFromOrigin(cards as EventOriginType[]);

export const formatCollections = (
    cards: CollectionCardOriginType[],
): CreatorCollectionCardCarouselProps[] =>
    cards.map((collection) => transformCollectionCard(collection));

const formatCards = (
    isEvent: boolean,
    cards: OriginCards,
    isFromDestination?: boolean,
): FormattedEvent[] | CreatorCollectionCardCarouselProps[] => {
    return isEvent
        ? formatEvents(
              cards as EventOriginType[] | DestinationEvent[],
              isFromDestination,
          )
        : formatCollections(cards as CollectionCardOriginType[]);
};

export const CardsCarousel: React.FC<RelatedCardsChildrenProps> = (props) => {
    const {
        cardsType,
        cards,
        title,
        newTitleStyle,
        affCode,
        isFromDestination,
        dataTestId,
    } = props;

    const isEvent: boolean = cardsType === CardsType.EVENT;
    const formattedCards = formatCards(isEvent, cards, isFromDestination);
    // BUGBOT (Medium): the hook inputs (context + destination upgrade) are
    // resolved HERE, at the top of the component, so the card-builder helpers
    // stay pure. The eb-ui source called hooks from `_getEventCards` (a helper
    // invoked mid-render) — legal only by accident of call order. Hoisting them
    // makes the Rules-of-Hooks contract explicit and order-independent.
    const context = (useContext(ApplicationContext) as CollectionServerData | null) ||
        undefined;
    const { organizerId: pageOwnerOrganizerId } = useContext(AnalyticsContext);
    const updatedEvents = isEvent
        ? useExtendedEventData(formattedCards as FormattedEvent[])
        : undefined;
    const CardMap = getCardMap({
        cardsType,
        formattedCards,
        affCode,
        updatedEvents,
        context,
        pageOwnerOrganizerId,
    });
    // Legacy: EVENTS = 3 desktop, COLLECTIONS = 2 desktop; mobile = 1.
    const slidesVisible: SlideConfig = {
        tablet: 2,
        desktop: isEvent ? 3 : 2,
    };
    // Legacy `isNotContinuous` + `hasTopControls` + `alwaysShowSideNavigation`:
    // the carousel nav controls render only when there are MORE cards than fit
    // on the desktop (multiple pages). For a small set that all fit (the common
    // "More collections from this organizer" case — 2-3 cards at 2-up), the live
    // page shows DISCRETE cards with a gap and NO navigation controls, so render
    // a static flex row instead of a scroll strip.
    const hasOverflow = CardMap.length > slidesVisible.desktop;

    const titleEl = (
        <span className={newTitleStyle ? 'cc-carousel-title--new' : ''}>
            {title}
        </span>
    );

    return (
        <section
            className="carousel-cards"
            data-testid={dataTestId}
            aria-label={title}
        >
            <div className="eds-g-cell eds-l-pad-hor-5 eds-l-ln-pad-hor-0 eds-l-lg-pad-hor-0 eds-l-lw-pad-hor-0 eds-l-mar-bot-16">
                {hasOverflow ? (
                    <SimpleCarousel
                        headerTitle={titleEl}
                        useSideNavigation={false}
                        fullyEnableNavigation
                        slidesVisible={slidesVisible}
                    >
                        {CardMap}
                    </SimpleCarousel>
                ) : (
                    <div className="cc-carousel-static">
                        <div className="cc-carousel-static__title">{titleEl}</div>
                        <div className="cc-carousel-static__row">
                            {CardMap}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};
